import { z } from 'zod';

// =============================================================================
// Form engine · JSON-driven multi-step form descriptor.
//
// A `FormConfig` is what each vertical exports to describe its public
// qualification form. The engine renders it (DynamicForm component) and the
// backend validates submissions against a Zod schema *built dynamically*
// from the same config (buildAnswersSchema). No duplication between FE/BE
// validation: one source of truth.
//
// Scope: a Field declares whether it writes to the Lead doc (name/email/phone)
// or to the QualificationSubmission.answers map. The engine routes the value
// to the correct destination at submit time.
//
// Conditional visibility: a Field can declare a `condition` that hides it
// unless another field has a specific value. Pure runtime · no JSX branching
// in vertical configs.
// =============================================================================

// -----------------------------------------------------------------------------
// Field types · kept tight on purpose. Add only when a new vertical genuinely
// needs a new kind. "single-choice" with icons covers most card-style picks.
// -----------------------------------------------------------------------------
export const formFieldType = z.enum([
  'text',
  'email',
  'phone',
  'number',
  'money-bracket', // grid of cards each carrying a value in cents (or null)
  'single-choice', // grid of cards (one selected)
  'yes-no',        // 2 cards: sim / não
  'stepper',       // numeric counter with +/- buttons
]);
export type FormFieldType = z.infer<typeof formFieldType>;

// -----------------------------------------------------------------------------
// Scope · where the value lands in the persisted record.
//   'lead'    → lead.{id}                       (name, email, phone)
//   'answers' → qualification_submission.answers.{id}   (most fields)
//   'meta'    → ignored by validation; consumed by the engine itself
//               (honeypot, hidden tracking)
// -----------------------------------------------------------------------------
export const formFieldScope = z.enum(['lead', 'answers', 'meta']);
export type FormFieldScope = z.infer<typeof formFieldScope>;

// -----------------------------------------------------------------------------
// Option in a choice / money-bracket field.
//
//   value:       primitive payload. For money-bracket, a number in cents (or
//                null meaning "I don't have any"). For single-choice, a string
//                or boolean.
//   label:       what the user sees.
//   description: subtitle in the card (e.g., "Faixa 2 do MCMV").
//   iconName:    optional lucide-react icon name. The renderer maps it.
// -----------------------------------------------------------------------------
export const formFieldOption = z
  .object({
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    label: z.string().min(1).max(120),
    description: z.string().max(200).optional(),
    iconName: z.string().max(40).optional(),
  })
  .strict();
export type FormFieldOption = z.infer<typeof formFieldOption>;

// -----------------------------------------------------------------------------
// Visibility condition · a field only renders when another field matches.
//
//   { field: 'incomeSource', equals: 'self_employed' }
//   { field: 'hasFGTS', equals: true }
//   { field: 'incomeSource', in: ['clt', 'self_employed'] }
// -----------------------------------------------------------------------------
export const formFieldCondition = z
  .object({
    field: z.string().min(1),
    equals: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    notEquals: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
    in: z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
  })
  .strict();
export type FormFieldCondition = z.infer<typeof formFieldCondition>;

// -----------------------------------------------------------------------------
// Per-field validation rules · translated into Zod constraints by
// buildAnswersSchema (server) and buildLeadSchema (client) at runtime.
//
// Keep this small. If a vertical needs something exotic (regex pra placa de
// carro), add a named validator below and reference by name.
// -----------------------------------------------------------------------------
export const formFieldValidation = z
  .object({
    required: z.boolean().optional(),
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(1).optional(),
    /** For number / stepper / money-bracket. */
    min: z.number().optional(),
    max: z.number().optional(),
    /** Named validator from a curated registry (e.g. 'br-phone', 'cep', 'placa-carro'). */
    namedValidator: z.string().max(40).optional(),
  })
  .strict();
export type FormFieldValidation = z.infer<typeof formFieldValidation>;

// -----------------------------------------------------------------------------
// FormField · a single question.
// -----------------------------------------------------------------------------
export const formField = z
  .object({
    /** Stable identifier. Becomes the key in lead/answers. snake_case or camelCase. */
    id: z.string().min(1).max(80).regex(/^[a-zA-Z][a-zA-Z0-9_]*$/),
    /**
     * Where the value lands on submit. Defaults to `'answers'` at runtime
     * (helpers and the engine read `field.scope ?? 'answers'`), so configs
     * can omit it for the common case of an answers-scope field.
     */
    scope: formFieldScope.optional(),
    type: formFieldType,
    /** Question shown to the user. */
    label: z.string().min(1).max(240),
    /** Helper text below the field. */
    hint: z.string().max(400).optional(),
    placeholder: z.string().max(120).optional(),
    /** For choice / money-bracket fields. */
    options: z.array(formFieldOption).max(20).optional(),
    /** Grid columns for cards-based renderers. 1 (full width), 2 (default), 3. */
    columns: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    validation: formFieldValidation.optional(),
    condition: formFieldCondition.optional(),
  })
  .strict();
export type FormField = z.infer<typeof formField>;

// -----------------------------------------------------------------------------
// FormStep · a screen of the multi-step form.
// -----------------------------------------------------------------------------
export const formStep = z
  .object({
    /** Stable identifier. */
    id: z.string().min(1).max(60),
    title: z.string().min(1).max(120),
    subtitle: z.string().max(400).optional(),
    fields: z.array(formField).min(1).max(30),
  })
  .strict();
export type FormStep = z.infer<typeof formStep>;

// -----------------------------------------------------------------------------
// FormConfig · the whole multi-step form for one vertical.
// -----------------------------------------------------------------------------
export const formConfig = z
  .object({
    /** Bumped when the config evolves so backwards-compat can be reasoned about. */
    version: z.literal(1),
    /** Vertical key this config belongs to (e.g., 'mcmv-imobiliario'). */
    verticalKey: z.string().min(2).max(40),
    /** Label of the final submit button. Defaults to 'Enviar respostas' at the render layer. */
    submitLabel: z.string().min(1).max(60).optional(),
    /** Title above the form on the public page. */
    title: z.string().min(1).max(120).optional(),
    /** Subtitle above the form. */
    subtitle: z.string().max(400).optional(),
    steps: z.array(formStep).min(1).max(8),
  })
  .strict();
export type FormConfig = z.infer<typeof formConfig>;

// -----------------------------------------------------------------------------
// Helpers used by the runtime (engine + qualifier) · kept here so the same
// utilities run server-side and in the browser.
// -----------------------------------------------------------------------------

/**
 * Returns every field across all steps, in declaration order. Useful for
 * iterating to build validators or flatten an answers object.
 */
export function flattenFields(config: FormConfig): FormField[] {
  return config.steps.flatMap((s) => s.fields);
}

/**
 * Returns the fields filtered by scope.
 */
export function fieldsByScope(
  config: FormConfig,
  scope: FormFieldScope,
): FormField[] {
  return flattenFields(config).filter((f) => (f.scope ?? 'answers') === scope);
}

/**
 * Evaluates a field's `condition` against the current answers state. Returns
 * `true` if the field should be visible/required.
 *
 * Conditions reference other fields by `id` only · the engine looks the value
 * up in either the lead-scope or answers-scope of the current state.
 */
export function isFieldVisible(
  field: FormField,
  state: Record<string, unknown>,
): boolean {
  if (!field.condition) return true;
  const observed = state[field.condition.field];
  if (field.condition.equals !== undefined) {
    return observed === field.condition.equals;
  }
  if (field.condition.notEquals !== undefined) {
    return observed !== field.condition.notEquals;
  }
  if (field.condition.in !== undefined) {
    return field.condition.in.includes(observed as never);
  }
  return true;
}

/**
 * Human label for an answer, as the lead saw it: the option label for choice
 * fields, the raw value otherwise. Used by WhatsApp messages and the panel so
 * neither keeps its own copy of the option texts.
 */
export function answerLabel(config: FormConfig, fieldId: string, value: unknown): string {
  const field = flattenFields(config).find((f) => f.id === fieldId);
  const option = field?.options?.find((o) => o.value === value);
  if (option) return option.label;
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return value === undefined || value === null || value === '' ? '·' : String(value);
}

// =============================================================================
// Zod schema builder · turns a FormConfig into a Zod schema that validates
// the `answers` object on the server. Mirrors validation rules declared in
// the config so we never duplicate logic between client and server.
//
// Conditional fields contribute to the schema as `.optional()` since they may
// legitimately be absent; deeper enforcement (was condition met but value
// missing?) happens in a second-pass refine.
// =============================================================================

function buildFieldSchema(field: FormField): z.ZodTypeAny {
  const v = field.validation ?? {};
  switch (field.type) {
    case 'text': {
      let s = z.string().trim();
      if (v.minLength) s = s.min(v.minLength);
      if (v.maxLength) s = s.max(v.maxLength);
      return s;
    }
    case 'email':
      return z.string().trim().toLowerCase().email();
    case 'phone':
      // Alinhado com phoneInput (common.ts) · 10 dígitos mínimo (DDD + fixo).
      return z.string().trim().min(10).max(20);
    case 'number':
    case 'stepper': {
      let s = z.number().int();
      if (v.min !== undefined) s = s.min(v.min);
      if (v.max !== undefined) s = s.max(v.max);
      return s;
    }
    case 'money-bracket': {
      // Option values are number-or-null; build a literal union from options.
      if (!field.options || field.options.length === 0) {
        return z.union([z.number().int().nonnegative(), z.null()]);
      }
      const literals = field.options.map((o) =>
        o.value === null
          ? z.literal(null)
          : z.literal(o.value as number),
      );
      return literals.length === 1
        ? literals[0]!
        : z.union(literals as unknown as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]);
    }
    case 'single-choice': {
      if (!field.options || field.options.length === 0) {
        return z.string();
      }
      const literals = field.options.map((o) => z.literal(o.value as never));
      return literals.length === 1
        ? literals[0]!
        : z.union(literals as unknown as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]);
    }
    case 'yes-no':
      return z.boolean();
  }
}

/**
 * Builds the Zod schema for the `answers` object of a submission. Conditional
 * fields are optional at the type level; presence enforcement (was condition
 * met → value required?) is done via a single `superRefine` pass that has
 * access to the entire answers state.
 */
export function buildAnswersSchema(config: FormConfig): z.ZodTypeAny {
  const answerFields = fieldsByScope(config, 'answers');
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const f of answerFields) {
    const fieldSchema = buildFieldSchema(f);
    // Optional in the type · superRefine handles "required when visible".
    shape[f.id] = fieldSchema.optional();
  }

  return z
    .object(shape)
    .passthrough() // tolerate forward-compat fields not yet declared
    .superRefine((data, ctx) => {
      const state = data as Record<string, unknown>;
      for (const f of answerFields) {
        if (!isFieldVisible(f, state)) continue;
        const value = state[f.id];
        const required = f.validation?.required ?? true;
        if (!required) continue;
        // `null` is a legitimate "I don't have any" answer for money-bracket
        // fields that explicitly offer it as an option (e.g., "Não tenho
        // ainda" for down-payment). Treat it as a valid response, not as
        // missing.
        const nullIsValidAnswer =
          f.type === 'money-bracket' &&
          (f.options?.some((o) => o.value === null) ?? false);
        const isMissing =
          value === undefined ||
          value === '' ||
          (value === null && !nullIsValidAnswer);
        if (isMissing) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [f.id],
            message: `Campo obrigatório: ${f.label}`,
          });
        }
      }
    });
}

/**
 * Builds the Zod schema for the lead-scoped fields (name, email, phone).
 * Returns a plain z.object so it can be merged with the wider submit schema
 * if a vertical needs to add lead-level custom fields later.
 */
export function buildLeadFieldsSchema(config: FormConfig): z.ZodObject<z.ZodRawShape> {
  const leadFields = fieldsByScope(config, 'lead');
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const f of leadFields) {
    shape[f.id] = buildFieldSchema(f);
  }
  return z.object(shape);
}
